import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosResponse } from 'axios';
import { useForm } from 'react-hook-form';
import useAPIError from '../../../hooks/useAPIError';
import DataService from '../../../services/MisDatosService';
import { useAuthStore as useAuthStoreEroski } from '../../../store/AuthContext';
import { Cookie, PersonalData, PrefijoPais } from '../../../types/PersonalData';
import ModalConfirmacion from '../../../components/ModalConfirmacion';
import { getCookies } from '../../../api/api';
import { ErrorCodeConstants } from '../../../utils/ErrorCodeConstants';
import { TextHelper } from '../../../utils/TextHelper';
import { IntervalUtils } from '../../../utils/IntervalUtils';
import IconBack from '../../../Icons/IconBack';
import IconChat from '../../../Icons/IconChat';
import { HostProvider } from '../../../utils/HostProvider';
import { MisDatosHelper } from '../MisDatosHelper';
import { MisDatosConstants } from '../../../utils/MisDatosConstants';
import { CryptoUtil } from '../../../utils/CryptoUtil';

interface Props {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  cargarPersonalData: () => void;
  cargarAccessData: () => void;
  setVisibleVerificacionDoble?: (visible: boolean) => void;
  prefijoPersonalData?: string;
  telefonoPersonalData?: string;
  opcion: string;
  personalData?: PersonalData;
  prefijosPaises: PrefijoPais[];
  setShowOroError?: (oroError: boolean) => void;
}

const ModalRecuperarPass: FC<Props> = (props) => {
  const {
    visible,
    setVisible,
    cargarPersonalData,
    cargarAccessData,
    setVisibleVerificacionDoble,
    prefijoPersonalData,
    telefonoPersonalData,
    opcion,
    personalData,
    prefijosPaises,
    setShowOroError,
  } = props;
  const dataUser = useAuthStoreEroski();
  const [prefijo, setPrefijo] = useState('' as string);
  const [tamTelf, setTamTelf] = useState(9 as number);
  const [telf, setTelf] = useState('' as string);
  const [codigo, setCodigo] = useState('' as string);
  const [recuerdame, setRecuerdame] = useState(0 as number);
  const { t } = useTranslation();
  const { addError } = useAPIError();
  const [endingError, setEndingError] = useState(false);
  const [showSMS, setShowSMS] = useState(false);
  const [showError, setShowError] = useState(false);
  const [cantSms, setCantSms] = useState<number>(0);
  const [showModal, setShowModal] = useState(false as boolean);
  const [reenvioRealizado, setReenvioRealizado] = useState(false as boolean);
  const titleActivation = t('datos.accessdata.sms-service-activation-title');
  const textActivation = t('datos.accessdata.sms-service-activation-message');
  const [titleError, setTitleError] = useState('' as string);
  const [messageError, setMessageError] = useState('' as string);
  const [errorType, setErrorType] = useState(0 as number);
  const acceptButtonTextKey = t('global.confirm');
  const errorAcceptButtonText = t('entendido');
  const isApp = dataUser.user.isApp === 'true';
  const { register, handleSubmit, errors, setValue, clearError } = useForm({
    mode: 'onChange',
  });
  const [load, setLoad] = useState<boolean>(false);
  const isCaprabo = HostProvider.isCaprabo();
  const misDatosHelper = new MisDatosHelper();

  const linkContacto = isCaprabo
    ? `${process.env.REACT_APP_CLUB_CAPRABO}atencion-cliente/`
    : `${process.env.REACT_APP_SERVER_EROSKI_ES}${t('link.contacto')}`;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const emptymethod = () => {};

  const setPrefijoTelefono = (value: string) => {
    if (value === '1' || value === '') {
      return '34';
    }
    return '350';
  };

  const setTamTelefono = (value: string) => {
    if (value === '1' || value === '') {
      return 9;
    }
    return 8;
  };

  const retrieveTelfAccessData = () => {
    DataService.getTelfDatosAcceso()
      .then((response) => {
        response.data.pojo.prefijo = CryptoUtil.decryptAes(
          response.data.pojo.prefijo,
        );
        response.data.pojo.telefono = CryptoUtil.decryptAes(
          response.data.pojo.telefono,
        );
        // @typescript-eslint/no-use-before-define
        const prefijoTelefono = setPrefijoTelefono(response.data.pojo.prefijo);
        const tamTelefono = setTamTelefono(response.data.pojo.prefijo);

        setTelf(response.data.pojo.telefono);
        setPrefijo(prefijoTelefono);
        setTamTelf(tamTelefono);
        const telefonoElement = document.getElementById(
          'pais-telefono-sms',
        ) as HTMLInputElement;
        if (telefonoElement !== null) {
          telefonoElement.value = response.data.pojo.telefono;
        }
      })
      .catch((e) => {
        console.log(e);
        addError(t('nlr-vaya-algo-no-ha-ido-bien'), 'KO');
      });
  };

  const setearVariables = () => {
    setCodigo('');
    setCantSms(0);
    setEndingError(false);
  };

  const cerrarModalYReiniciarVariables = () => {
    setShowSMS(false);
    setVisible(false);
    setearVariables();
    retrieveTelfAccessData();
  };

  const cerrarModalErrores = () => {
    setShowSMS(false);
    setVisible(true);
    setShowError(false);
    setearVariables();
  };

  const acceptError = () => {
    setShowError(false);
    if (
      errorType ===
      ErrorCodeConstants.SEND_SMS_DATOSACCESO_NUMERO_EXISTE_EXCEPTION
    ) {
      cerrarModalErrores();
    } else if (cantSms < 5 && !endingError) {
      if (
        errorType ===
        ErrorCodeConstants.UPDATE_DATOSACCESO_DOBLE_VERIFICACION_INCORRECT_PASSWORD_EXCEPTION
      ) {
        setShowSMS(true);
      } else if (errorType === ErrorCodeConstants.SYSTEM_EXCEPTION) {
        if (setShowOroError) {
          setShowOroError(true);
        }
      }
    } else {
      cerrarModalYReiniciarVariables();
    }
  };

  const retrievePrefijoByCountry = (e: React.FormEvent<HTMLSelectElement>) => {
    const selectedId = Number(e.currentTarget.value);
    prefijosPaises.forEach((currentValue) => {
      if (currentValue.id === selectedId) {
        setPrefijo(currentValue.prefijo);
        setTamTelf(currentValue.tamanioTelefono);
        setValue('paisTelefono', ``);
      }
    });
  };

  const HandleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
  ) => {
    // eslint-disable-next-line default-case
    switch (key) {
      case 'codigo':
        setCodigo(e.target.value);
        setValue('inputCodigo', `${e.target.value}`);
        break;
      case 'confiar':
        setRecuerdame(e.target.checked ? 1 : 0);
        break;
    }
  };

  const crearCookieDobleVerificacion = (response: AxiosResponse<any>) => {
    const cookie = response.data.pojo as Cookie;
    if (cookie) {
      if (isCaprabo) {
        getCookies().set(cookie.cookieName, cookie.cookieValue, {
          domain: '.caprabo.com',
        });
      } else {
        getCookies().set(cookie.cookieName, cookie.cookieValue, {
          domain: '.eroski.es',
        });
      }
    }
  };

  const actualizarDatosPersonales = (tipoUsuario: string) => {
    if (personalData !== undefined) {
      personalData.tipoCambioMail = 'MD'; // Origen cambio de email form update personal data
      DataService.updatePersonalData(personalData, tipoUsuario)
        .then((res: AxiosResponse) => {
          if (res.data.error === true) {
            addError(t('nlr-vaya-algo-no-ha-ido-bien'), 'WARNING');
          } else {
            setCodigo('');
            document.body.classList.remove('fix');
            if (setVisibleVerificacionDoble) {
              setVisibleVerificacionDoble(false);
            }
          }
        })
        .catch((er: any) => {
          console.log(er);
          addError(t('nlr-vaya-algo-no-ha-ido-bien'), 'WARNING');
        });
    }
  };

  const gestionErrorUpdateDobleVerificacion = (
    response: AxiosResponse<any>,
  ) => {
    setTitleError(
      t(TextHelper.getErrorTitleKeyTransformed(response.data.title)),
    );
    setMessageError(
      t(TextHelper.getErrorMessageKeyTransformed(response.data.userMessage)),
    );
    setErrorType(response.data.errorCode);
    setShowError(true);
    if (
      response.data.errorCode ===
      ErrorCodeConstants.UPDATE_DATOSACCESO_DOBLE_VERIFICACION_INCORRECT_PASSWORD_EXCEPTION
    ) {
      setCantSms(cantSms + 1);
    }
  };

  const getVerificationCode = async () => {
    return (await DataService.getSmsCode()).data.pojo.code;
  };

  const setSmsCodeValueToField = (code: string) => {
    const smsElement = document.getElementById('tfn2-sms') as HTMLInputElement;
    if (smsElement !== null && code !== null) {
      smsElement.value = code;
      setCodigo(code);
      setValue('inputCodigo', code);
      clearError('inputCodigo');
    }
  };

  const pullServiceForCode = () => {
    IntervalUtils.setIntervalForFunctionWithResponse(
      'interval',
      5,
      4000,
      getVerificationCode,
      setSmsCodeValueToField,
    );
  };

  const sendSMS = (isReenvio: boolean) => {
    const data = {
      prefijo: opcion === 'datosPersonales' ? prefijoPersonalData : prefijo,
      telefono: opcion === 'datosPersonales' ? telefonoPersonalData : telf,
      tipo: 'AC',
      fromApp: isApp ? 'app_club' : 'web',
      version: '',
      idDispositivo: '',
      longitud: '',
      latitud: '',
      mobileDevice: false,
      ip: '',
    };
    setVisible(false);
    DataService.sendSmsDobleVerif(data).then((response) => {
      if (response.data.error === true) {
        if (
          response.data.errorCode ===
          ErrorCodeConstants.SEND_SMS_DATOSACCESO_NUMERO_EXISTE_EXCEPTION
        ) {
          gestionErrorUpdateDobleVerificacion(response);
        } else if (response.data.error) {
          addError(t('nlr-vaya-algo-no-ha-ido-bien'), 'WARNING');
        }
      } else {
        setShowSMS(true);
        setVisible(false);
        if (dataUser.user.isApp === 'true') {
          pullServiceForCode();
        }
        if (isReenvio) {
          setReenvioRealizado(true);
        } else {
          setReenvioRealizado(false);
        }
      }
    });
  };

  const updateDatosAcceso = () => {
    const data = {
      tipo: 'AC',
      fromApp: isApp ? 'app_club' : 'web',
      codigo,
      dobleVerificacion: true,
      recuerdame,
    };
    if (cantSms < 5) {
      setVisible(false);
      setShowSMS(false);
      DataService.updateDatosAcceso(data)
        .then((response) => {
          // setShowSMS(false);
          // setVisible(false);
          if (response.data.error === true) {
            if (
              response.data.errorCode === ErrorCodeConstants.SYSTEM_EXCEPTION
            ) {
              setMessageError(t('nlr-vaya-algo-no-ha-ido-bien'));
              setErrorType(response.data.errorCode);
              setShowError(true);
              // addError(t('nlr-vaya-algo-no-ha-ido-bien'), 'WARNING');
            } else {
              gestionErrorUpdateDobleVerificacion(response);
            }
          } else {
            crearCookieDobleVerificacion(response);
            setEndingError(false);
            if (opcion === 'datosPersonales') {
              actualizarDatosPersonales(
                misDatosHelper.switchTipoUsuario(personalData?.tipoUsuario),
              );
            } else {
              setCodigo('');
              if (setVisibleVerificacionDoble) {
                setVisibleVerificacionDoble(false);
              }
            }
            cargarPersonalData();
            cargarAccessData();
            setShowModal(true);
          }
        })
        .catch((er: any) => {
          console.log(er);
          addError(t('nlr-vaya-algo-no-ha-ido-bien'), 'WARNING');
        });
    }
  };

  //* *** Save with Enter key
  const saveInfo = (e) => {
    if (e.key === 'Enter' && e.keyCode === 13) {
      const element = document.querySelector(
        'div.modal.modal--small.is-active',
      ) as HTMLElement;
      element.removeEventListener('keydown', saveInfo, false);
      e.preventDefault();
      e.stopPropagation();
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      document.getElementById('accept-btn').click();
    }
  };

  useEffect(() => {
    if (dataUser.user.token !== '') {
      retrieveTelfAccessData();
    }
    if (prefijo !== '') {
      prefijosPaises.forEach((currentValue) => {
        if (currentValue.prefijo === prefijo) {
          setTamTelf(currentValue.tamanioTelefono);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUser.user.token]);

  useEffect(() => {
    if (visible || showSMS) {
      if (visible) {
        retrieveTelfAccessData();
      }

      const element = document.querySelector(
        'div.modal.modal--small.is-active',
      ) as HTMLElement;
      if (element !== null) {
        element.setAttribute('tabindex', '0');
        element.focus();
        element.addEventListener('keydown', saveInfo, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, showSMS, prefijo]);

  useEffect(() => {
    if (dataUser.user.token !== '') {
      retrieveTelfAccessData();
    }
    if (prefijo !== '') {
      prefijosPaises.forEach((currentValue) => {
        if (currentValue.prefijo === prefijo) {
          setTamTelf(currentValue.tamanioTelefono);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUser.user.token]);

  useEffect(() => {
    setLoad(visible);
  }, [telf, visible]);

  useEffect(() => {
    if (dataUser.user.token !== '') {
      retrieveTelfAccessData();
    }
    if (prefijo !== '') {
      prefijosPaises.forEach((currentValue) => {
        if (currentValue.prefijo === prefijo) {
          setTamTelf(currentValue.tamanioTelefono);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUser.user.token]);

  const dataModalRecuperar = () => {
    const valueTel =
      // eslint-disable-next-line no-nested-ternary
      opcion === 'datosPersonales'
        ? `${telefonoPersonalData}` === 'null'
          ? ''
          : `${telefonoPersonalData}`
        : `${telf}` === 'null'
        ? ''
        : `${telf}`;
    return (
      <>
        <div
          className={`modal modal--small ${visible ? 'is-active' : ''}`}
          id="modal-recuperar"
        >
          <div className="modal__background" />
          <div className="modal__card u-text-center">
            <header className="modal__card-head u-flex">
              <div className="modal__card-title">
                {' '}
                {t('datos.accessdata.verificar-telf')}
              </div>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
              <a
                className="modal__card-close modal-cerrar"
                onClick={() => {
                  setVisible(false);
                  retrieveTelfAccessData();
                }}
              >
                <span className="hidden">Icono para cerrar modal</span>
                <div className="icon icon-medium icon--close">
                  <IconBack />
                </div>
              </a>
            </header>
            <div className="modal__card-body" id="myform">
              <form
                id="form-datos-movil"
                className="form-datos"
                onSubmit={handleSubmit(() => {
                  sendSMS(false);
                })}
              >
                <fieldset>
                  <div className="form-group select-wrapper u-margin-bottom">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      htmlFor="movil"
                      className="control-label u-text-left u-full-width"
                    >
                      {t('datos.accessdata.celular')}
                    </label>
                    <div className="u-display-flex">
                      <div className="form-control-block form-control--compound">
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label className="hidden" htmlFor="movil">
                          Selecciona el prefijo
                        </label>
                        <select
                          id="pais-telefono"
                          className="form-control pais-telefono"
                          onChange={(e) => {
                            retrievePrefijoByCountry(e);
                          }}
                        >
                          {opcion === 'datosPersonales' ? (
                            <option>{prefijo}</option>
                          ) : (
                            prefijosPaises &&
                            prefijosPaises.map((p) =>
                              p.id === MisDatosConstants.ID_PREFIJO_ESPANIA ||
                              p.id === MisDatosConstants.ID_PREFIJO_GIBRALTAR ||
                              p.id === MisDatosConstants.ID_PREFIJO_ANDORRA ||
                              p.id ===
                                MisDatosConstants.ID_PREFIJO_INGLATERRA ? (
                                <option
                                  selected={p.prefijo === prefijo}
                                  key={p.id}
                                  value={p.id}
                                >
                                  {p.prefijo}
                                </option>
                              ) : (
                                ''
                              ),
                            )
                          )}
                        </select>
                        <div className="icon icon-small">
                          <IconChat />
                        </div>
                      </div>
                      <div className="form-control input-select u-text-left">
                        <input
                          type="tel"
                          className={`form-control ${
                            errors.paisTelefono ? 'error' : ''
                          }`}
                          name="paisTelefono"
                          id="pais-telefono-sms"
                          maxLength={tamTelf}
                          defaultValue={load ? valueTel : ''}
                          onChange={($event) => {
                            setTelf($event.target.value);
                          }}
                          ref={register({
                            required: true,
                            validate: (value) =>
                              misDatosHelper.testTelefono(
                                `pais-telefono`,
                                value,
                              ),
                          })}
                        />
                        {errors.paisTelefono &&
                          errors.paisTelefono.type === 'required' && (
                            // eslint-disable-next-line jsx-a11y/label-has-associated-control
                            <label
                              id="pais-telefono-sms-error"
                              className="error"
                              htmlFor="pais-telefono-sms"
                            >
                              {t('validaciones.empty-value')}
                            </label>
                          )}
                        {errors.paisTelefono &&
                          errors.paisTelefono.type === 'validate' && (
                            // eslint-disable-next-line jsx-a11y/label-has-associated-control
                            <label
                              id="pais-telefono-sms-error"
                              className="error"
                              htmlFor="pais-telefono-sms"
                            >
                              {t(misDatosHelper.prefixErrorValidation(prefijo))}
                            </label>
                          )}
                      </div>
                    </div>
                  </div>

                  <div className="modal__botonera">
                    <button
                      id="accept-btn"
                      type="submit"
                      className="btn btn-primary u-margin-bottom"
                    >
                      {t('datos.accessdata.accept')}
                    </button>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                    <a
                      className="btn btn-secundary"
                      onClick={() => {
                        setShowSMS(false);
                        setVisible(false);
                        retrieveTelfAccessData();
                      }}
                    >
                      {t('datos.accessdata.cancelar')}
                    </a>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </>
    );
  };

  const dataModalSms = () => {
    return (
      <div
        className={`modal modal--small ${showSMS ? 'is-active' : ''}`}
        id="modal-sms"
      >
        <div className="modal__background" />
        <div className="modal__card u-text-center">
          <header className="modal__card-head u-flex">
            <div className="modal__card-title">
              {' '}
              {t('datos.accessdata.verificar-telf')}
            </div>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <a
              className="modal__card-close modal-cerrar"
              onClick={() => {
                setShowSMS(false);
              }}
            >
              <span className="hidden">Icono para cerrar modal</span>
              <div className="icon icon-medium icon--close">
                <IconBack />
              </div>
            </a>
          </header>
          <div className="modal__card-body">
            <form
              id="form-datos-movil-verificar"
              className="form-datos"
              onSubmit={handleSubmit(() => {
                updateDatosAcceso();
              })}
            >
              <fieldset>
                <div className="form-group select-wrapper u-margin-bottom">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="control-label u-text-left u-full-width u-margin-bottom">
                    {t('datos.accessdata.sms-sended')}
                  </label>
                  <div className="form-control-block u-margin-bottom">
                    <input
                      type="text"
                      className={`form-control ${
                        errors.inputCodigo ? 'error' : ''
                      }`}
                      name="inputCodigo"
                      id="tfn2-sms"
                      maxLength={5}
                      onChange={(e) => HandleChange(e, 'codigo')}
                      ref={register({
                        required: true,
                        pattern: /^[0-9]+$/i,
                        minLength: 5,
                      })}
                    />
                    {errors.inputCodigo &&
                      errors.inputCodigo.type === 'required' && (
                        // eslint-disable-next-line jsx-a11y/label-has-associated-control
                        <label
                          id="tfn2-sms-error"
                          className="error"
                          htmlFor="tfn2-sms"
                        >
                          {t('validaciones.empty-value')}
                        </label>
                      )}
                    {errors.inputCodigo &&
                      errors.inputCodigo.type === 'pattern' && (
                        // eslint-disable-next-line jsx-a11y/label-has-associated-control
                        <label
                          id="tfn2-sms-error"
                          className="error"
                          htmlFor="tfn2-sms"
                        >
                          {t('validaciones.not-valid-value')}
                        </label>
                      )}
                    {errors.inputCodigo &&
                      errors.inputCodigo.type === 'minLength' && (
                        // eslint-disable-next-line jsx-a11y/label-has-associated-control
                        <label
                          id="tfn2-sms-error"
                          className="error"
                          htmlFor="tfn2-sms"
                        >
                          {t('validaciones.not-valid-code')}
                        </label>
                      )}
                  </div>
                  <div className="form-group u-margin-bottom u-text-left">
                    <label className="checkgroup" htmlFor="confiar">
                      {t('datos.accessdata.trust-browser')}

                      <input
                        type="checkbox"
                        id="confiar"
                        onChange={(e) => HandleChange(e, 'confiar')}
                      />
                      <span className="checkmark" />
                    </label>
                  </div>
                  {!reenvioRealizado ? (
                    <p className="u-margin-bottom u-text-left">
                      {t('datos.accessdata.not-sms-received')}{' '}
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a
                        href="#"
                        className="link u-display-inline"
                        onClick={() => sendSMS(true)}
                      >
                        {t('datos.accessdata.re-send')}
                      </a>
                    </p>
                  ) : (
                    <p className="u-margin-bottom u-text-left">
                      {t('datos.accessdata.re-sended-part1')}{' '}
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a href={linkContacto} className="link u-display-inline">
                        {t('datos.accessdata.re-sended-url')}
                      </a>{' '}
                      {t('datos.accessdata.re-sended-part2')}
                    </p>
                  )}
                  <div className="modal__botonera">
                    <button
                      id="accept-btn"
                      type="submit"
                      className="btn btn-primary u-margin-bottom"
                    >
                      {t('datos.accessdata.accept')}
                    </button>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                    <a
                      className="btn btn-secundary"
                      onClick={() => {
                        setShowSMS(false);
                        setVisible(false);
                        IntervalUtils.clearInterval('interval');
                      }}
                    >
                      {t('datos.accessdata.cancelar')}
                    </a>
                  </div>
                </div>
              </fieldset>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {(() => {
        if (visible) {
          return dataModalRecuperar();
        }
        if (showSMS) {
          return dataModalSms();
        }
        return '';
      })()}
      <ModalConfirmacion
        isBig={false}
        isSmall
        textCentered
        showModal={showModal}
        setShowModal={setShowModal}
        title={t(titleActivation)}
        textHTMLContent={t(textActivation)}
        doAceptar={emptymethod}
        doClose={emptymethod}
        showCancel={false}
        acceptButtonTextKey={acceptButtonTextKey}
      />
      <ModalConfirmacion
        isBig={false}
        isSmall
        textCentered
        showModal={showError}
        setShowModal={setShowError}
        title={t(titleError)}
        textHTMLContent={t(messageError)}
        doAceptar={acceptError}
        doClose={emptymethod}
        showCancel={false}
        acceptButtonTextKey={errorAcceptButtonText}
      />
    </>
  );
};

export default ModalRecuperarPass;
